import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cpApi } from '../api/api';
import { useTransactionsFilters } from '../context/TransactionsFilterContext';
import { useDashboardFilter } from '../context/useDashboardFilter';
import type { DashboardStatsResponse } from '../types';

function formatRub(n: number) {
  return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function formatUsdt(n: number) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function formatClosingTime(sec: number): string {
  if (sec < 60) return `${sec} сек`;
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  if (min < 60) return s > 0 ? `${min} мин ${s} сек` : `${min} мин`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { isResponsible } = useTransactionsFilters() ?? { isResponsible: false };
  const dashboardFilter = useDashboardFilter();
  const [data, setData] = useState<DashboardStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isResponsible) navigate('/requests', { replace: true });
  }, [isResponsible, navigate]);

  const fetchData = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await cpApi.getDashboardStats({ from, to });
      setData(res);
    } catch {
      setError('Ошибка загрузки данных');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const range = dashboardFilter?.range ?? { from: '', to: '' };
  useEffect(() => {
    if (isResponsible && range.from && range.to) fetchData(range.from, range.to);
    else setLoading(false);
  }, [range.from, range.to, fetchData, isResponsible]);

  return (
    <div className="p-6 space-y-6 w-full">

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400">Загрузка...</div>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
              <div className="text-sm text-blue-600 dark:text-blue-400">Всего выплат</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{data.total_payouts}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
              <div className="text-sm text-green-600 dark:text-green-400">Оборот (RUB)</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatRub(data.total_rub)}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
              <div className="text-sm text-amber-600 dark:text-amber-400">Оборот (USDT)</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatUsdt(data.total_usdt)}</div>
            </div>
            {(data.total_canceled ?? 0) > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-red-200 dark:border-red-800">
                <div className="text-sm text-red-600 dark:text-red-400">Отменено мэтчей</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{data.total_canceled}</div>
              </div>
            )}
            {typeof data.average_closing_sec === 'number' && data.average_closing_sec > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">Среднее время закрытия</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatClosingTime(data.average_closing_sec)}</div>
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">По направлению</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                <div className="font-medium text-blue-800 dark:text-blue-200">C2C</div>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Выплат: {data.by_direction.c2c.count} · RUB: {formatRub(data.by_direction.c2c.rub)} · USDT: {formatUsdt(data.by_direction.c2c.usdt)}
                  {(data.by_direction.c2c.canceled_count ?? 0) > 0 && (
                    <span className="text-red-600 dark:text-red-400 ml-1"> · Отменено: {data.by_direction.c2c.canceled_count}</span>
                  )}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
                <div className="font-medium text-indigo-800 dark:text-indigo-200">SBP</div>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Выплат: {data.by_direction.sbp.count} · RUB: {formatRub(data.by_direction.sbp.rub)} · USDT: {formatUsdt(data.by_direction.sbp.usdt)}
                  {(data.by_direction.sbp.canceled_count ?? 0) > 0 && (
                    <span className="text-red-600 dark:text-red-400 ml-1"> · Отменено: {data.by_direction.sbp.canceled_count}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {data.by_account && data.by_account.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700 w-full">
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">По аккаунту</h3>
              <div className="overflow-x-auto w-full">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600 text-left text-gray-600 dark:text-gray-400">
                      <th className="pb-2 pr-4">Аккаунт</th>
                      <th className="pb-2 pr-4 text-right">Кол-во</th>
                      <th className="pb-2 pr-4 text-right">RUB</th>
                      <th className="pb-2 pr-4 text-right">Мин (RUB)</th>
                      <th className="pb-2 pr-4 text-right">Макс (RUB)</th>
                      <th className="pb-2 pr-4 text-right">USDT</th>
                      <th className="pb-2 pr-4 text-right">Мин (USDT)</th>
                      <th className="pb-2 pr-4 text-right">Макс (USDT)</th>
                      {(data.by_account.some((a) => (a.canceled_count ?? 0) > 0)) && (
                        <th className="pb-2 text-right">Отменено</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_account.map((acc) => (
                      <tr key={acc.counterparty_id} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white">{acc.name || `#${acc.counterparty_id}`}</td>
                        <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{acc.count}</td>
                        <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{formatRub(acc.rub)}</td>
                        <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{typeof acc.min_rub === 'number' ? formatRub(acc.min_rub) : '—'}</td>
                        <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{typeof acc.max_rub === 'number' ? formatRub(acc.max_rub) : '—'}</td>
                        <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{formatUsdt(acc.usdt)}</td>
                        <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{typeof acc.min_usdt === 'number' ? formatUsdt(acc.min_usdt) : '—'}</td>
                        <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{typeof acc.max_usdt === 'number' ? formatUsdt(acc.max_usdt) : '—'}</td>
                        {(data.by_account.some((a) => (a.canceled_count ?? 0) > 0)) && (
                          <td className="py-2 text-right text-gray-700 dark:text-gray-300">{(acc.canceled_count ?? 0) > 0 ? acc.canceled_count : '—'}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.total_payouts === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              За выбранный период данных нет.
            </div>
          )}
        </>
      )}
    </div>
  );
}
